<script lang="ts" setup>
    import { ref, computed } from 'vue';

    const props = defineProps<{
        modelValue: number | string | boolean | undefined | 'Yes' | 'No',
        min: number | undefined,
        max: number | undefined,
        options: string[] | string | undefined
    }>();
    const emit = defineEmits(["update", "next"]);

    // How far (in degrees) the dial extends past the valid min/max on each side.
    const RANGE_BUFFER = 20;

    // Some checks (e.g. Cook Temperature) only configure a minimum - "must be at least X,
    // no upper limit" - so max (or min) can legitimately be missing. Treat that side as
    // open-ended rather than defaulting it to 0, which would otherwise invert min/max.
    const hasMin = computed(() => props.min !== undefined && props.min !== null);
    const hasMax = computed(() => props.max !== undefined && props.max !== null);

    // Fractions (0-1) of the track width given to the under-range, valid-range and over-range zones.
    // The valid zone always gets the same share of the track regardless of how many degrees it
    // spans, so a small valid range (e.g. a 5 degree fridge target) drags slowly while the wider
    // buffer zones either side drag through their degrees much faster. A side with no configured
    // limit has no "bad" state to warn about, so it gets no buffer zone - the valid zone just
    // runs all the way to that edge of the track.
    const ZONE_MARGIN = 0.25;
    const zoneStart = computed(() => hasMin.value ? ZONE_MARGIN : 0);
    const zoneEnd = computed(() => hasMax.value ? 1 - ZONE_MARGIN : 1);

    // An open-ended side still needs a finite number to anchor the dial and its pips to -
    // give it the same span as the fast-scroll buffer on a bounded side would have.
    const OPEN_END_SPAN = RANGE_BUFFER * 2;
    const effectiveMin = computed(() => {
        if (hasMin.value) return props.min as number;
        if (hasMax.value) return (props.max as number) - OPEN_END_SPAN;
        return 0;
    });
    const effectiveMax = computed(() => {
        if (hasMax.value) return props.max as number;
        if (hasMin.value) return (props.min as number) + OPEN_END_SPAN;
        return 0;
    });
    const absoluteMin = computed(() => hasMin.value ? effectiveMin.value - RANGE_BUFFER : effectiveMin.value);
    const absoluteMax = computed(() => hasMax.value ? effectiveMax.value + RANGE_BUFFER : effectiveMax.value);

    // Readings are selected to one decimal place (0.1 degree steps).
    const roundToStep = (value: number): number => Math.round(value * 10) / 10;

    // With no value loaded yet, start the handle at the middle of the valid range. When only one
    // side is configured there's no real midpoint (the range is open-ended), so start right at
    // the configured threshold instead of the arbitrary open end.
    const defaultValue = () => {
        if (hasMin.value && hasMax.value) return roundToStep((effectiveMin.value + effectiveMax.value) / 2);
        if (hasMin.value) return effectiveMin.value;
        if (hasMax.value) return effectiveMax.value;
        return 0;
    };
    const hasModelValue = props.modelValue !== undefined && props.modelValue !== null && props.modelValue !== '' && !isNaN(Number(props.modelValue));

    const temperature = ref<number>(hasModelValue ? roundToStep(Number(props.modelValue)) : defaultValue());
    const wasSkipped = ref(false);
    const dragging = ref(false);
    const trackEl = ref<HTMLElement | null>(null);

    const valueToFraction = (value: number): number => {
        if (value <= absoluteMin.value) return 0;
        if (value >= absoluteMax.value) return 1;
        if (value < effectiveMin.value) {
            if (effectiveMin.value === absoluteMin.value) return zoneStart.value;
            return zoneStart.value * (value - absoluteMin.value) / (effectiveMin.value - absoluteMin.value);
        }
        if (value <= effectiveMax.value) {
            if (effectiveMax.value === effectiveMin.value) return zoneStart.value;
            return zoneStart.value + (zoneEnd.value - zoneStart.value) * (value - effectiveMin.value) / (effectiveMax.value - effectiveMin.value);
        }
        if (absoluteMax.value === effectiveMax.value) return zoneEnd.value;
        return zoneEnd.value + (1 - zoneEnd.value) * (value - effectiveMax.value) / (absoluteMax.value - effectiveMax.value);
    }
    const fractionToValue = (fraction: number): number => {
        let raw: number;
        if (fraction <= zoneStart.value) {
            raw = zoneStart.value === 0
                ? effectiveMin.value
                : absoluteMin.value + (fraction / zoneStart.value) * (effectiveMin.value - absoluteMin.value);
        } else if (fraction <= zoneEnd.value) {
            raw = zoneEnd.value === zoneStart.value
                ? effectiveMin.value
                : effectiveMin.value + (fraction - zoneStart.value) / (zoneEnd.value - zoneStart.value) * (effectiveMax.value - effectiveMin.value);
        } else {
            raw = zoneEnd.value === 1
                ? effectiveMax.value
                : effectiveMax.value + (fraction - zoneEnd.value) / (1 - zoneEnd.value) * (absoluteMax.value - effectiveMax.value);
        }
        const rounded = roundToStep(raw);
        return Math.min(absoluteMax.value, Math.max(absoluteMin.value, rounded));
    }

    const handlePercent = computed(() => valueToFraction(temperature.value) * 100);
    const validZoneStyle = computed(() => ({
        left: (zoneStart.value * 100) + '%',
        width: ((zoneEnd.value - zoneStart.value) * 100) + '%'
    }));

    // A pip (tick mark) every N degrees inside the valid range, so the slow-scrolling zone
    // reads like a ruler. The wide buffer zones outside it only get their two end labels.
    // N is chosen so there are always roughly TARGET_PIP_COUNT pips, whether the valid range
    // is a tight 5 degrees (fridge) or a wide 25+ degrees (freezer) - a fixed step either
    // crowds a wide range or barely marks a narrow one.
    const TARGET_PIP_COUNT = 5;
    const NICE_STEPS = [1, 2, 5, 10, 20, 25, 50, 100];
    const pipCountForStep = (step: number): number => {
        const lo = Math.ceil(effectiveMin.value / step) * step;
        const hi = Math.floor(effectiveMax.value / step) * step;
        return hi >= lo ? Math.floor((hi - lo) / step) + 1 : 0;
    };
    const pipStep = computed(() => {
        if (effectiveMax.value <= effectiveMin.value) return NICE_STEPS[0];
        let best = NICE_STEPS[NICE_STEPS.length - 1];
        let bestDiff = Infinity;
        for (const step of NICE_STEPS) {
            const diff = Math.abs(pipCountForStep(step) - TARGET_PIP_COUNT);
            if (diff < bestDiff) {
                bestDiff = diff;
                best = step;
            }
        }
        return best;
    });
    const pipValues = computed(() => {
        const step = pipStep.value;
        const lo = Math.ceil(effectiveMin.value / step) * step;
        const hi = Math.floor(effectiveMax.value / step) * step;
        const pips: number[] = [];
        for (let v = lo; v <= hi; v += step) pips.push(v);
        return pips;
    });

    // Scale text: the two outer bounds, the midpoint of each outer (fast-scroll) buffer zone -
    // only when that side actually has one - and every whole degree inside the valid range.
    const midUnder = computed(() => Math.round((absoluteMin.value + effectiveMin.value) / 2));
    const midOver = computed(() => Math.round((effectiveMax.value + absoluteMax.value) / 2));
    const scaleLabels = computed(() => {
        const values = [
            absoluteMin.value,
            ...(hasMin.value ? [midUnder.value] : []),
            ...pipValues.value,
            ...(hasMax.value ? [midOver.value] : []),
            absoluteMax.value
        ];
        const seen = new Set<number>();
        return values.filter(v => (seen.has(v) ? false : (seen.add(v), true)));
    });

    // One-time "new control" tip, shown only to users who hit this screen during the rollout
    // window (01-11 Oct 2026). Dismissing it - or the window passing - hides it for good;
    // it's not meant to linger as a permanent explainer.
    const TIP_DISMISSED_KEY = 'cm_temp_dial_tip_dismissed';
    const TIP_WINDOW_START = new Date(2026, 9, 1); // months are 0-indexed: 9 = October
    const TIP_WINDOW_END = new Date(2026, 9, 11, 23, 59, 59);
    const tipAlreadyDismissed = (): boolean => {
        try {
            return localStorage.getItem(TIP_DISMISSED_KEY) === '1';
        } catch {
            return true; // storage unavailable - fail safe to "don't show" rather than nag every load
        }
    };
    const now = new Date();
    const showTip = ref(now >= TIP_WINDOW_START && now <= TIP_WINDOW_END && !tipAlreadyDismissed());
    const dismissTip = () => {
        showTip.value = false;
        try {
            localStorage.setItem(TIP_DISMISSED_KEY, '1');
        } catch {
            // storage unavailable - dismissal just won't persist across reloads
        }
    };

    const validateValue = (value: string): 'under' | 'over' | 'invalid' | 'ok' | 'ignore' => {
        if (value == undefined) return "invalid";
        const val = parseFloat(value)
        if (props.min != undefined && val < props.min) return "under";
        if (props.max != undefined && val > props.max) return "over";
        return "ok";
    }
    const status = computed(() => validateValue(temperature.value.toString()));
    const statusLabel = computed(() => {
        switch (status.value) {
            case 'under': return 'Below range';
            case 'over': return 'Above range';
            case 'ok': return 'In range';
            default: return '';
        }
    });

    const onValueChange = () => {
        emit('update')
    }

    const updateFromClientX = (clientX: number) => {
        if (!trackEl.value) return;
        const rect = trackEl.value.getBoundingClientRect();
        const fraction = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        temperature.value = fractionToValue(fraction);
    }
    const onPointerMove = (e: PointerEvent) => {
        if (!dragging.value) return;
        updateFromClientX(e.clientX);
    }
    const onPointerUp = () => {
        if (!dragging.value) return;
        dragging.value = false;
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        onValueChange();
    }
    const onTrackPointerDown = (e: PointerEvent) => {
        dragging.value = true;
        updateFromClientX(e.clientX);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    }

    const load = (value: string | number | boolean | undefined) => {
        if (value != undefined) {
            if (value == "Skipped") {
                wasSkipped.value = true;
            } else {
                temperature.value = roundToStep(parseFloat(value.toString())) || 0;
            }
        } else {
            temperature.value = defaultValue();
        }
    }
    const validate = () :  'under' | 'over' | 'invalid' | 'ok' | 'ignore' => {
        if (wasSkipped.value) return "ignore";
        return validateValue(temperature.value.toString())
    }
    const value = () => {
        if (wasSkipped.value) return "Skipped";
        return temperature.value.toString();
    }
    const skip = () => {
        wasSkipped.value = true;
    }
    defineExpose({ validate, load, value, skip });
</script>

<template>
    <div class="temp-dial">
        <div v-if="showTip" class="temp-dial-tip">
            <span>New: We've improved the temperature selector to make it faster for you to select a temperature. Drag the slider to set the temperature.</span>
            <button type="button" class="temp-dial-tip-dismiss" @click="dismissTip">Got it</button>
        </div>

        <div class="temp-dial-value">
            <span class="temp-dial-number">{{ temperature.toFixed(1) }}</span><span class="temp-dial-unit">&deg;C</span>
        </div>
        <div v-if="statusLabel" class="temp-dial-status" :class="status">
            <span v-if="status === 'ok'">&#10003; {{ statusLabel }}</span>
            <span v-else>&#9888; {{ statusLabel }}</span>
        </div>

        <div class="temp-dial-track-wrap" @pointerdown="onTrackPointerDown">
            <div class="temp-dial-track" ref="trackEl">
                <div class="temp-dial-track-valid" :style="validZoneStyle"></div>
                <span
                    v-for="pip in pipValues"
                    :key="pip"
                    class="temp-dial-pip"
                    :style="{ left: (valueToFraction(pip) * 100) + '%' }"
                ></span>
            </div>
            <div class="temp-dial-handle" :style="{ left: handlePercent + '%' }">
                <div class="temp-dial-handle-line temp-dial-handle-line--top"></div>
                <div class="temp-dial-handle-dot"></div>
                <div class="temp-dial-handle-line temp-dial-handle-line--bottom"></div>
            </div>
        </div>

        <div class="temp-dial-labels">
            <span
                v-for="label in scaleLabels"
                :key="label"
                class="temp-dial-label"
                :style="{ left: (valueToFraction(label) * 100) + '%' }"
            >{{ label }}&deg;</span>
        </div>
    </div>
</template>

<style scoped>
    .temp-dial {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        padding: 10px 0 20px;
    }

    .temp-dial-tip {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 85%;
        margin-bottom: 14px;
        padding: 8px 12px;
        border-radius: 10px;
        background: #eaf6fc;
        border: 1px solid #bfe3f3;
        color: #151740;
        font-size: 0.9em;
    }

    .temp-dial-tip span {
        flex: 1;
    }

    .temp-dial-tip-dismiss {
        flex-shrink: 0;
        border: none;
        background: none;
        color: #009de0;
        font-weight: 700;
        font-size: 1em;
        padding: 4px 6px;
        cursor: pointer;
    }

    .temp-dial-value {
        display: flex;
        align-items: flex-end;
        gap: 4px;
        line-height: 1;
    }

    .temp-dial-number {
        font-size: 4.5em;
        font-weight: 800;
        color: #151740;
    }

    .temp-dial-unit {
        font-size: 1.4em;
        font-weight: 600;
        color: #8a8aa3;
        margin-bottom: 0.2em;
    }

    .temp-dial-status {
        margin-top: 6px;
        padding: 4px 16px;
        border-radius: 999px;
        font-weight: 700;
        font-size: 1em;
        border: 1.5px solid transparent;
    }

    .temp-dial-status.ok {
        color: #79b342;
        border-color: #bfe3a0;
        background: #f1f8ea;
    }

    .temp-dial-status.under,
    .temp-dial-status.over {
        color: #e1341e;
        border-color: #f3b3ab;
        background: #fdeeec;
    }

    .temp-dial-track-wrap {
        position: relative;
        z-index: 2;
        box-sizing: content-box;
        width: 85%;
        margin-top: 20px;
        /* Real padding (not a sibling's margin) so the handle's hit area is part of this
           element's own box and pointerdown fires no matter where on the handle you grab it. */
        padding: 34px 0 16px;
        touch-action: none;
    }

    .temp-dial-track {
        position: relative;
        height: 48px;
        border-radius: 999px;
        background: #e7e7ee;
    }

    .temp-dial-track-valid {
        position: absolute;
        top: 0;
        bottom: 0;
        border-radius: 999px;
        background: #d7ecc0;
        border-top: 5px solid #79b342;
        border-bottom: 5px solid #79b342;
    }

    .temp-dial-pip {
        position: absolute;
        top: 8px;
        bottom: 8px;
        width: 2px;
        background: rgba(255, 255, 255, 0.7);
        transform: translateX(-50%);
        border-radius: 1px;
    }

    .temp-dial-handle {
        position: absolute;
        top: 0;
        transform: translateX(-50%);
        pointer-events: none;
    }

    .temp-dial-handle-line {
        width: 2px;
        margin: 0 auto;
        background: #79b342;
    }

    .temp-dial-handle-line--top {
        height: 25px;
        margin-top: 10px;
    }

    /* Continues past the track and over the scale below it, like a pin through the numbers. */
    .temp-dial-handle-line--bottom {
        height: 25px;
    }

    .temp-dial-handle-dot {
        width: 48px;
        height: 48px;
        margin: 0 auto;
        border-radius: 50%;
        background: #fff;
        border: 5px solid #79b342;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
    }

    .temp-dial-labels {
        position: relative;
        z-index: 1;
        width: 85%;
        margin-top: 10px;
        height: 16px;
    }

    .temp-dial-label {
        position: absolute;
        transform: translateX(-50%);
        white-space: nowrap;
        font-size: 0.85em;
        color: #9a9ab0;
    }
</style>
